import { z } from 'zod';
import { BuildPopulationKeySchema } from './runs';

/**
 * QD-1 — Deterministic build-scoped quality decision.
 * QD-2 — Explicit freeze + keep-once historical snapshot (decisionMode LIVE|FROZEN).
 * QD-3 — Deterministic FROZEN↔FROZEN decision diff (compose-on-read; no persistence).
 * QD-4 — Frozen-QD acceptance consumption (compose-on-read; no new decision).
 *
 * Identity: (projectId, buildId, commitSha, environment)
 * Policy: exactly one pinned Required Scenario Policy version.
 * Precedence: FAILED > INSUFFICIENT_EVIDENCE > PASSED
 *
 * AI / QI / risk / gates / Release Confidence are never authority.
 * claimsCryptographicBinding remains false — application-level only.
 */

export const QD1_DECISION_VERDICTS = ['APPROVED', 'NOT_APPROVED', 'INSUFFICIENT_EVIDENCE'] as const;
export type Qd1DecisionVerdict = (typeof QD1_DECISION_VERDICTS)[number];

export const QD1_SCENARIO_RESULTS = ['PASSED', 'FAILED', 'INSUFFICIENT_EVIDENCE'] as const;
export type Qd1ScenarioResult = (typeof QD1_SCENARIO_RESULTS)[number];

export const QD1_DECISION_MODES = ['LIVE', 'FROZEN'] as const;
export type Qd1DecisionMode = (typeof QD1_DECISION_MODES)[number];

export const QD1_SCENARIO_REASON_CODES = [
  'qualifying_pass',
  'authoritative_fail',
  'no_execution',
  'cancelled_only',
  'blocked_only',
  'skipped_only',
  'missing_proof',
  'unverified_proof',
  'integrity_failure',
  'version_stale',
  'scenario_unavailable',
  'non_terminal_only',
  'no_qualifying_pass',
] as const;
export type Qd1ScenarioReasonCode = (typeof QD1_SCENARIO_REASON_CODES)[number];

export const QD1_DECISION_REASON_CODES = [
  'explicit_empty_policy',
  'all_required_passed',
  'required_scenario_failed',
  'incomplete_build_identity',
  'policy_pin_missing',
  'policy_missing',
  'policy_conflict',
  'policy_invalid',
  'policy_version_not_found',
  'required_scenario_insufficient',
] as const;
export type Qd1DecisionReasonCode = (typeof QD1_DECISION_REASON_CODES)[number];

export const Qd1EvaluateQuerySchema = z.object({
  buildId: z.string().trim().min(1).max(120).optional(),
  commitSha: z.string().trim().min(1).max(64).optional(),
  environment: z.string().trim().min(1).max(200).optional(),
  /**
   * When no pin exists yet, this may create a keep-once pin.
   * When a pin already exists, evaluation uses the pin (never active).
   * Never silently falls back to active_version.
   */
  policyVersion: z.coerce.number().int().positive().optional(),
});
export type Qd1EvaluateQuery = z.infer<typeof Qd1EvaluateQuerySchema>;

export const Qd1PinPolicyRequestSchema = BuildPopulationKeySchema.omit({ projectId: true }).extend({
  policyVersion: z.number().int().positive(),
});
export type Qd1PinPolicyRequest = z.infer<typeof Qd1PinPolicyRequestSchema>;

export const Qd1PinPolicyResponseSchema = z.object({
  projectId: z.string().uuid(),
  buildId: z.string().min(1).max(120),
  commitSha: z.string().min(7).max(64),
  environment: z.string().min(1).max(200),
  policyVersion: z.number().int().positive(),
  created: z.boolean(),
});
export type Qd1PinPolicyResponse = z.infer<typeof Qd1PinPolicyResponseSchema>;

/** QD-2 — explicit freeze body (complete identity required). */
export const Qd1FreezeRequestSchema = z.object({
  buildId: z.string().trim().min(1).max(120),
  commitSha: z.string().trim().min(7).max(64),
  environment: z.string().trim().min(1).max(200),
  /** Optional: pin-if-absent before freeze; never overrides existing pin. */
  policyVersion: z.number().int().positive().optional(),
});
export type Qd1FreezeRequest = z.infer<typeof Qd1FreezeRequestSchema>;

export const Qd1FrozenQuerySchema = z.object({
  buildId: z.string().trim().min(1).max(120),
  commitSha: z.string().trim().min(7).max(64),
  environment: z.string().trim().min(1).max(200),
});
export type Qd1FrozenQuery = z.infer<typeof Qd1FrozenQuerySchema>;

export const Qd1ScenarioDecisionSchema = z.object({
  scenarioId: z.string().uuid(),
  result: z.enum(QD1_SCENARIO_RESULTS),
  reasonCode: z.enum(QD1_SCENARIO_REASON_CODES),
  executionIdsConsidered: z.array(z.string().uuid()).default([]),
  failedExecutionIds: z.array(z.string().uuid()).default([]),
  passedExecutionId: z.string().uuid().nullable().default(null),
  /** Selected execution used for PASS/FAIL contribution (freeze-time). */
  executionId: z.string().uuid().nullable().optional(),
  runId: z.string().uuid().nullable().optional(),
  /** QP-1 Proof is composed from this execution id when present. */
  proofExecutionId: z.string().uuid().nullable().optional(),
  scenarioStatus: z.string().nullable().default(null),
  /**
   * LIVE: current scenario content version (may drift).
   * FROZEN: version recorded at freeze (immutable).
   */
  scenarioContentVersion: z.number().int().positive().nullable().default(null),
  /** Version that was evaluated for this scenario contribution (required on FROZEN). */
  evaluatedScenarioVersion: z.number().int().positive().nullable().optional(),
  evidenceIntegrityStatus: z.enum(['verified', 'unverified']).nullable().default(null),
  versionStale: z.boolean().default(false),
});
export type Qd1ScenarioDecision = z.infer<typeof Qd1ScenarioDecisionSchema>;

export const Qd1DecisionResponseSchema = z.object({
  /** QD-2 — LIVE recomputation vs FROZEN keep-once snapshot. */
  decisionMode: z.enum(QD1_DECISION_MODES),
  verdict: z.enum(QD1_DECISION_VERDICTS),
  reasonCode: z.enum(QD1_DECISION_REASON_CODES),
  projectId: z.string().uuid(),
  buildId: z.string().nullable(),
  commitSha: z.string().nullable(),
  environment: z.string().nullable(),
  policyVersion: z.number().int().positive().nullable(),
  requiredScenarioCount: z.number().int().nonnegative(),
  scenarios: z.array(Qd1ScenarioDecisionSchema).default([]),
  /** Present only when decisionMode === FROZEN. */
  frozenAt: z.string().datetime().nullable().optional(),
  /** Honesty: application-level decision, not cryptographic non-repudiation. */
  claimsCryptographicBinding: z.literal(false),
  claimsSealedHistory: z.literal(false),
  /** Advisory surfaces must not affect this verdict. */
  aiIsNotAuthority: z.literal(true),
  qiIsNotAuthority: z.literal(true),
  riskIsNotAuthority: z.literal(true),
  gatesAreNotAuthority: z.literal(true),
  releaseConfidenceIsNotAuthority: z.literal(true),
});
export type Qd1DecisionResponse = z.infer<typeof Qd1DecisionResponseSchema>;

export const Qd1FreezeResponseSchema = Qd1DecisionResponseSchema.extend({
  decisionMode: z.literal('FROZEN'),
  frozenAt: z.string().datetime(),
  /** True when this request created the keep-once row. */
  created: z.boolean(),
});
export type Qd1FreezeResponse = z.infer<typeof Qd1FreezeResponseSchema>;

/** QD-3 — closed deterministic attribution taxonomy (no causal/regression labels). */
export const QD3_ATTRIBUTION_CODES = [
  'POLICY_SCOPE_CHANGED',
  'SCENARIO_ADDED',
  'SCENARIO_REMOVED',
  'SCENARIO_VERSION_CHANGED',
  'OUTCOME_CHANGED',
  'EXECUTION_CHANGED',
  'PROOF_CHANGED',
  'INTEGRITY_CHANGED',
  'REASON_CHANGED',
  'VERDICT_CHANGED',
  'UNCHANGED',
] as const;
export type Qd3AttributionCode = (typeof QD3_ATTRIBUTION_CODES)[number];

export const QD3_COMPARISON_MODES = ['FROZEN_TO_FROZEN'] as const;
export type Qd3ComparisonMode = (typeof QD3_COMPARISON_MODES)[number];

/** QD-3 — compare query: baseline → candidate (same project from route). */
export const Qd1CompareQuerySchema = z.object({
  baselineBuildId: z.string().trim().min(1).max(120),
  baselineCommitSha: z.string().trim().min(7).max(64),
  baselineEnvironment: z.string().trim().min(1).max(200),
  candidateBuildId: z.string().trim().min(1).max(120),
  candidateCommitSha: z.string().trim().min(7).max(64),
  candidateEnvironment: z.string().trim().min(1).max(200),
});
export type Qd1CompareQuery = z.infer<typeof Qd1CompareQuerySchema>;

export const Qd3CompareSideSchema = z.object({
  projectId: z.string().uuid(),
  buildId: z.string().min(1).max(120),
  commitSha: z.string().min(7).max(64),
  environment: z.string().min(1).max(200),
  frozenAt: z.string().datetime(),
  verdict: z.enum(QD1_DECISION_VERDICTS),
  reasonCode: z.enum(QD1_DECISION_REASON_CODES),
  policyVersion: z.number().int().positive(),
  requiredScenarioCount: z.number().int().nonnegative(),
});
export type Qd3CompareSide = z.infer<typeof Qd3CompareSideSchema>;

export const Qd3ScenarioDeltaSchema = z.object({
  scenarioId: z.string().uuid(),
  presentInBaseline: z.boolean(),
  presentInCandidate: z.boolean(),
  baselineResult: z.enum(QD1_SCENARIO_RESULTS).nullable(),
  candidateResult: z.enum(QD1_SCENARIO_RESULTS).nullable(),
  outcomeChanged: z.boolean(),
  baselineEvaluatedScenarioVersion: z.number().int().positive().nullable(),
  candidateEvaluatedScenarioVersion: z.number().int().positive().nullable(),
  evaluatedScenarioVersionChanged: z.boolean(),
  baselineExecutionId: z.string().uuid().nullable(),
  candidateExecutionId: z.string().uuid().nullable(),
  baselineRunId: z.string().uuid().nullable(),
  candidateRunId: z.string().uuid().nullable(),
  executionChanged: z.boolean(),
  baselineProofExecutionId: z.string().uuid().nullable(),
  candidateProofExecutionId: z.string().uuid().nullable(),
  proofChanged: z.boolean(),
  baselineEvidenceIntegrityStatus: z.enum(['verified', 'unverified']).nullable(),
  candidateEvidenceIntegrityStatus: z.enum(['verified', 'unverified']).nullable(),
  integrityChanged: z.boolean(),
  baselineReasonCode: z.enum(QD1_SCENARIO_REASON_CODES).nullable(),
  candidateReasonCode: z.enum(QD1_SCENARIO_REASON_CODES).nullable(),
  reasonChanged: z.boolean(),
  attributionCodes: z.array(z.enum(QD3_ATTRIBUTION_CODES)).min(1),
});
export type Qd3ScenarioDelta = z.infer<typeof Qd3ScenarioDeltaSchema>;

export const Qd3CompareSummarySchema = z.object({
  policyChanged: z.boolean(),
  requiredScenarioCountBaseline: z.number().int().nonnegative(),
  requiredScenarioCountCandidate: z.number().int().nonnegative(),
  scenariosAdded: z.number().int().nonnegative(),
  scenariosRemoved: z.number().int().nonnegative(),
  /** Scenarios with any non-UNCHANGED attribution (includes add/remove). */
  scenariosChanged: z.number().int().nonnegative(),
  verdictChanged: z.boolean(),
  attributionCodes: z.array(z.enum(QD3_ATTRIBUTION_CODES)),
});
export type Qd3CompareSummary = z.infer<typeof Qd3CompareSummarySchema>;

export const Qd1CompareResponseSchema = z.object({
  comparisonMode: z.literal('FROZEN_TO_FROZEN'),
  baseline: Qd3CompareSideSchema,
  candidate: Qd3CompareSideSchema,
  summary: Qd3CompareSummarySchema,
  scenarioDeltas: z.array(Qd3ScenarioDeltaSchema),
  claimsCryptographicBinding: z.literal(false),
  claimsSealedHistory: z.literal(false),
  aiIsNotAuthority: z.literal(true),
  qiIsNotAuthority: z.literal(true),
  riskIsNotAuthority: z.literal(true),
  gatesAreNotAuthority: z.literal(true),
  releaseConfidenceIsNotAuthority: z.literal(true),
});
export type Qd1CompareResponse = z.infer<typeof Qd1CompareResponseSchema>;

/** QD-4 — frozen-only acceptance consumption (not human approval / deploy auth). */
export const QD4_ACCEPTANCE_MODES = ['FROZEN'] as const;
export type Qd4AcceptanceMode = (typeof QD4_ACCEPTANCE_MODES)[number];

export const QD4_ACCEPTANCE_STATUSES = ['ACCEPTED', 'NOT_ACCEPTED'] as const;
export type Qd4AcceptanceStatus = (typeof QD4_ACCEPTANCE_STATUSES)[number];

/** Same complete identity as frozen read — no optional fields. */
export const Qd4AcceptanceQuerySchema = Qd1FrozenQuerySchema;
export type Qd4AcceptanceQuery = z.infer<typeof Qd4AcceptanceQuerySchema>;

export const Qd4AcceptanceResponseSchema = z.object({
  acceptanceMode: z.literal('FROZEN'),
  acceptanceStatus: z.enum(QD4_ACCEPTANCE_STATUSES),
  /** Underlying frozen QD-1 verdict — never collapsed. */
  frozenVerdict: z.enum(QD1_DECISION_VERDICTS),
  frozenReasonCode: z.enum(QD1_DECISION_REASON_CODES),
  projectId: z.string().uuid(),
  buildId: z.string().min(1).max(120),
  commitSha: z.string().min(7).max(64),
  environment: z.string().min(1).max(200),
  policyVersion: z.number().int().positive(),
  requiredScenarioCount: z.number().int().nonnegative(),
  frozenAt: z.string().datetime(),
  claimsCryptographicBinding: z.literal(false),
  claimsSealedHistory: z.literal(false),
  /** Explicit non-authority of parallel release/gate/advisory surfaces. */
  aiIsNotAuthority: z.literal(true),
  qiIsNotAuthority: z.literal(true),
  riskIsNotAuthority: z.literal(true),
  gatesAreNotAuthority: z.literal(true),
  releaseConfidenceIsNotAuthority: z.literal(true),
  humanApprovalIsNotAuthority: z.literal(true),
  deploymentIsNotAuthorizedByThis: z.literal(true),
});
export type Qd4AcceptanceResponse = z.infer<typeof Qd4AcceptanceResponseSchema>;

/**
 * Current Build Identity — mutable selection pointer per (project, environment).
 * Not a builds product. Explicit SET only. Never inferred from latest run/SHA/pin.
 * Identity authority remains test_runs + BuildPopulationKey / isAuthoritativeBuildIdentity.
 */
export const CURRENT_BUILD_SOURCES = ['user', 'ci'] as const;
export type CurrentBuildSource = (typeof CURRENT_BUILD_SOURCES)[number];

export const CURRENT_BUILD_UNAVAILABLE_REASON_CODES = [
  'not_set',
  'environment_required',
  'incomplete_pointer',
  'project_forbidden',
] as const;
export type CurrentBuildUnavailableReasonCode = (typeof CURRENT_BUILD_UNAVAILABLE_REASON_CODES)[number];

export const CurrentBuildGetQuerySchema = z.object({
  environment: z.string().trim().min(1).max(200).optional(),
});
export type CurrentBuildGetQuery = z.infer<typeof CurrentBuildGetQuerySchema>;

export const CurrentBuildSetRequestSchema = z.object({
  environment: z.string().trim().min(1).max(200),
  buildId: z.string().trim().min(1).max(120),
  commitSha: z.string().trim().min(7).max(64),
  source: z.enum(CURRENT_BUILD_SOURCES).default('user'),
});
export type CurrentBuildSetRequest = z.infer<typeof CurrentBuildSetRequestSchema>;

export const CurrentBuildClearQuerySchema = z.object({
  environment: z.string().trim().min(1).max(200),
});
export type CurrentBuildClearQuery = z.infer<typeof CurrentBuildClearQuerySchema>;

export const CurrentBuildAvailableResponseSchema = z.object({
  status: z.literal('available'),
  identity: BuildPopulationKeySchema,
  updatedAt: z.string().datetime(),
  updatedBy: z.string().uuid().nullable(),
  source: z.enum(CURRENT_BUILD_SOURCES),
});
export type CurrentBuildAvailableResponse = z.infer<typeof CurrentBuildAvailableResponseSchema>;

export const CurrentBuildUnavailableResponseSchema = z.object({
  status: z.literal('unavailable'),
  reasonCode: z.enum(CURRENT_BUILD_UNAVAILABLE_REASON_CODES),
  identity: z.null(),
});
export type CurrentBuildUnavailableResponse = z.infer<typeof CurrentBuildUnavailableResponseSchema>;

export const CurrentBuildGetResponseSchema = z.discriminatedUnion('status', [
  CurrentBuildAvailableResponseSchema,
  CurrentBuildUnavailableResponseSchema,
]);
export type CurrentBuildGetResponse = z.infer<typeof CurrentBuildGetResponseSchema>;

export const CurrentBuildSetResponseSchema = CurrentBuildAvailableResponseSchema;
export type CurrentBuildSetResponse = z.infer<typeof CurrentBuildSetResponseSchema>;

export const CurrentBuildClearResponseSchema = z.object({
  status: z.literal('cleared'),
  projectId: z.string().uuid(),
  environment: z.string().min(1).max(200),
  cleared: z.boolean(),
});
export type CurrentBuildClearResponse = z.infer<typeof CurrentBuildClearResponseSchema>;
